import React from "react";
import { Coordinate } from "ol/coordinate";
import Feature from "ol/Feature";
import { useEffect, useRef } from "react";
import Style from "ol/style/Style";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { useMap } from "../../hooks/incontext/useMap";
import { Point } from "ol/geom";
import { fromLonLat } from "ol/proj";
import Text from "ol/style/Text";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import { Annotation } from ".";
import { useInteractionEvent } from "../../hooks";
import { Geometry } from "ol/geom";

export interface TextMarkerProps extends Annotation {
  center: Coordinate;
}

export const TextMarker = ({
  center,
  color = "BLUE",
  properties = {},
  onClick,
  onHover,
  onLeave,
  zIndex = 0,
  children,
  opacity = 1,
  isDisabledSelection = false,
}: TextMarkerProps) => {
  const map = useMap();
  const annotationRef = useRef<Feature<Point>>(
    new Feature(new Point(fromLonLat(center)))
  );

  const annotationLayerRef = useRef<VectorLayer<VectorSource<Feature<Geometry>>>>(
    new VectorLayer({
      source: new VectorSource({
        wrapX: false,
        features: [annotationRef.current],
      }),
    })
  );

  function onHoverHandler(feature: Feature) {
    if (feature) {
      if (onHover) {
        onHover({ annotation: annotationRef.current, properties });
      }
    }
  }

  function onClickHandler() {
    if (onClick) {
      onClick({
        annotation: annotationRef.current,
        properties,
      });
    }
  }

  // center 업데이트
  useEffect(() => {
    if (annotationRef.current) {
      const geometry = annotationRef.current.getGeometry() as Point;
      geometry.setCoordinates(fromLonLat(center));
    }
  }, [center]);

  // 레이어 라이프사이클 (map에 추가/제거만)
  useEffect(() => {
    const vectorLayer = annotationLayerRef.current;
    vectorLayer.setZIndex(zIndex);
    map.addLayer(vectorLayer);
    return () => {
      map.removeLayer(vectorLayer);
    };
  }, [map]);

  // 스타일 업데이트 (레이어 재생성 없이 setStyle만)
  useEffect(() => {
    annotationRef.current.setStyle(
      new Style({
        text: new Text({
          text: children ? children.props.children : "",
          font: `${children?.props?.size || 15}px Arial`,
          fill: new Fill({
            color: children?.props?.color ? children.props.color : "white",
          }),
          overflow: true,
          offsetX: 0,
          offsetY: -15,
          stroke: children?.props?.outline
            ? new Stroke({
                color: `white`,
                width: 3,
              })
            : undefined,
        }),
      })
    );
  }, [children, color, opacity]);

  // zIndex 업데이트
  useEffect(() => {
    if (annotationLayerRef.current && zIndex) {
      annotationLayerRef.current.setZIndex(zIndex);
    }
  }, [zIndex]);

  useInteractionEvent({
    annotation: annotationLayerRef.current,
    onClick: onClickHandler,
    onHover: onHoverHandler,
    isDisabledSelection,
  });

  // 프로퍼티 업데이트
  useEffect(() => {
    annotationRef.current.setProperties({
      ...properties,
      shape: "TextMarker",
      isModifying: false,
      source: annotationLayerRef.current.getSource(),
      layer: annotationLayerRef.current,
    });
  }, [properties]);

  return null;
};
